import { ComponentChildren, createContext } from 'preact';
import { useContext, useMemo } from 'preact/hooks';
import { useOffersSelector, OffersState } from '../offers-bridge';

type ApiEnvironment = {
  apiUrl: string;
  legoUrl: string;
  shopifyAppUrl: string;
  trackingUrl: string;
};

export type Auth = Exclude<OffersState['auth'], false>;

type ApiContextValue = {
  auth: Auth;
  environment: ApiEnvironment;
};

const ApiContext = createContext<ApiContextValue | null>(null);

export function useApiContext(): ApiContextValue {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApiContext must be used within ApiProvider');
  }
  return context;
}

function getShopifyAppUrl(envName: string | undefined): string {
  return envName === 'prod' ? 'https://shop-app.ordergroove.com' : 'https://staging.shopify.ordergroove.com';
}

function getTrackingUrl(envName: string | undefined): string {
  return envName === 'prod' ? 'https://collect.ordergroove.com' : 'https://staging.collect.ordergroove.com';
}

export function ApiProvider({ children }: { children: ComponentChildren }) {
  const auth = useOffersSelector(state => state.auth);
  const apiUrl = useOffersSelector(state => state.environment?.apiUrl);
  const legoUrl = useOffersSelector(state => state.environment?.legoUrl);
  const envName = useOffersSelector(state => state.environment?.name);

  const apiEnv = useMemo(
    () =>
      apiUrl && legoUrl
        ? { apiUrl, legoUrl, shopifyAppUrl: getShopifyAppUrl(envName), trackingUrl: getTrackingUrl(envName) }
        : null,
    [apiUrl, legoUrl, envName]
  );

  // don't render children until auth and environment are available, so all child components can assume they're available
  if (!auth || !apiEnv) return null;

  return <ApiContext.Provider value={{ auth, environment: apiEnv }}>{children}</ApiContext.Provider>;
}
